import React, { useCallback, ReactElement, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { Formik, Form } from 'formik';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { ErrorsCode } from '../../../../common/analytics/enum/log-errors-code';
import { logEvent, logEventError } from '../../../../common/analytics/helpers/log-event';
import GenericButton from '../../../../common/components/generic-button/generic-button';
import GenericPasswordField from '../../../../common/components/generic-password-field/generic-password-field';
import GenericTextField from '../../../../common/components/generic-text-field/generic-text-field';
import StandardDialog from '../../../../common/components/standard-dialog/standard-dialog';
import { AuthUserType, useLoginMutation } from '../../../../common/generated/graphql';
import { getUserId } from '../../../../common/helpers/get-user-id/get-user-id';
import handleLoginAuth from '../../../../common/helpers/handle-login-auth/handle-login-auth';
import { isTokenExpired } from '../../../../common/helpers/is-token-expired/is-token-expired';
import LoginButtonContent from '../login-button-content/login-button-content';
import { useStyles } from './login-form.styles';

export default function LoginForm(): ReactElement {
  const { replace } = useHistory();

  const classes = useStyles();

  const [openErrorDialogWithMessage, setOpenErrorDialogWithMessage] = useState<string>('');
  const onErrorDialogWithMessageClose = useCallback(() => {
    setOpenErrorDialogWithMessage('');
  }, []);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Insira um endereço de email válido')
      .required('Esse campo é obrigatório'),
    password: Yup.string()
      .required('Esse campo é obrigatório')
      .min(6, 'A senha deve ter pelo menos 6 caracteres'),
  });

  const [execLogin, { loading }] = useLoginMutation();
  const onSubmit = useCallback(
    async (values: { email: string; password: string }) => {
      const result = await execLogin({
        variables: {
          input: { email: values.email, password: values.password, userType: AuthUserType.Crm },
        },
      });

      const accessToken = result.data?.loginTokenAuth?.accessToken;

      if (accessToken && !isTokenExpired(accessToken)) {
        logEvent('login', { method: 'standard', user_id: getUserId(accessToken) });
        handleLoginAuth(accessToken);
        replace('/menu');
      } else {
        logEventError({ error_type: 'login', error_code: ErrorsCode.LOGIN_TOKEN_ERROR });
        setOpenErrorDialogWithMessage(
          'Ocorreu um erro! Por favor, confira os dados e tente novamente.'
        );
      }
    },
    [execLogin, replace]
  );

  return (
    <Box className={classes.formWrapper}>
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <Form id='login-form' className={classes.form}>
          <Box className={classes.input}>
            <GenericTextField id='email' label='Email' name='email' fieldType='email' />
          </Box>
          <Box className={classes.input}>
            <GenericPasswordField id='password' label='Senha' name='password' />
          </Box>
          <GenericButton
            id='login-submit-button'
            type='submit'
            color='primary'
            className={classes.submit}
          >
            <LoginButtonContent isLoading={loading} text='Entrar' />
          </GenericButton>
        </Form>
      </Formik>
      <Box className={classes.resetPassword}>
        <Typography display='inline' variant='body2'>
          Esqueceu a senha?
          <Link id='login-create-account-link' to='/reset-password' className={classes.link}>
            <Typography display='inline' variant='body2' color='primary'>
              Recuperar
            </Typography>
          </Link>
        </Typography>
      </Box>
      <StandardDialog
        id='login-error-dialog'
        title='Ops'
        text={openErrorDialogWithMessage}
        open={openErrorDialogWithMessage !== undefined && openErrorDialogWithMessage !== ''}
        onCloseClick={onErrorDialogWithMessageClose}
      />
    </Box>
  );
}
